import { ConnectEmailMethod } from '@/constants';
import { useGlobalState } from '@/hooks';
import type { ConnectFailedReason } from '@/modules/pmt/components/ESConnectFailedDialog/ESConnectFailedDialog';
import { GoogleOAuthFlow, useGoogleOAuth } from '@/modules/pmt/components/GoogleLoginButton/hooks';
import { EmailSyncGoogleApiScopes } from '@/modules/pmt/constants';
import { useEmailSyncContext } from '@/modules/pmt/context';
import { toggleEmailSyncAccount } from '@/modules/pmt/fetchers';
import { type IEmailAccount, ToggleEmailSyncAccountErrorCode } from '@/modules/pmt/interfaces';
import { gtmClickEsyncConnect, gtmConnectEmailSuccess, gtmEsyncDisconnectConfirm } from '@/utils';
import type { CodeResponse } from '@react-oauth/google';
import { useStatsigClient } from '@statsig/react-bindings';
import { useState } from 'react';

type TUseToggleEmailSync = {
  email: IEmailAccount;
  onToggledEmail: () => Promise<void>;
};

export const useToggleEmailSync = ({ email, onToggledEmail }: TUseToggleEmailSync) => {
  const { userInfo } = useGlobalState();
  const [showPopup, setShowPopup] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const { setConnectEsFailedReason, setSyncingAccount } = useEmailSyncContext();
  const { logEvent } = useStatsigClient();

  const reconnectEmailSyncSuccessHandler = (emailId: string) => {
    // Show "Syncing in progress banner"
    // Ref: https://www.pivotaltracker.com/story/show/187111995
    setSyncingAccount(emailId);
    gtmConnectEmailSuccess(ConnectEmailMethod.ToggleOn);
  };

  const handleOAuthSuccess = async (response: CodeResponse) => {
    if (!userInfo?.id) return;
    const { code } = response;
    try {
      setIsToggling(true);
      const res = await toggleEmailSyncAccount({
        emailAccountId: email.id,
        realtimeSyncActivated: true,
        code,
        redirectUri: 'postmessage',
      });

      if (res?.data?.realtime_sync_activated) {
        reconnectEmailSyncSuccessHandler(email.id);
      }
      // Re-fetch email accounts
      await onToggledEmail();
      // biome-ignore lint/suspicious/noExplicitAny: catch clause
    } catch (err: any) {
      const errorCode = err?.errors?.code;
      setConnectEsFailedReason(errorCode);
      console.error('Toggle email sync failed', err);
    } finally {
      setIsToggling(false);
    }
  };

  const { login: retriggerGoogleOAuth } = useGoogleOAuth({
    onSuccess: handleOAuthSuccess,
    onError: (error) => console.error({ error }),
    flow: GoogleOAuthFlow.AuthCode,
    redirect_uri: 'postmessage',
    scope: EmailSyncGoogleApiScopes,
  });

  const toggleEmailSync = async () => {
    if (!userInfo?.id) return;

    setIsToggling(true);

    try {
      const res = await toggleEmailSyncAccount({
        emailAccountId: email.id,
        realtimeSyncActivated: !email.realtimeSyncActivated,
      });

      if (res?.status === 400 && res?.errors?.code) {
        const errorCode = res.errors.code as ConnectFailedReason;
        setConnectEsFailedReason(errorCode);
        return;
      }

      // When user tries to reconnect ES but the token is invalid,
      // open Google Authentication flow for user to reauthenticate their email
      if (!email.realtimeSyncActivated && res?.errors?.code === ToggleEmailSyncAccountErrorCode.INVALID_TOKEN) {
        await retriggerGoogleOAuth();
        return;
      }

      if (res?.data?.realtime_sync_activated) {
        reconnectEmailSyncSuccessHandler(email.id);
      }
      // Re-fetch email accounts
      await onToggledEmail();
      // biome-ignore lint/suspicious/noExplicitAny: catch clause
    } catch (err: any) {
      const errorCode = err?.errors?.code;
      setConnectEsFailedReason(errorCode);
      console.error('Toggle email sync failed', err);
    } finally {
      setIsToggling(false);
    }
  };

  const handleToggle = async () => {
    // disconnected -> connect
    if (!email.realtimeSyncActivated) {
      await toggleEmailSync();
      gtmClickEsyncConnect({ method: ConnectEmailMethod.ToggleOn });
      return;
    }

    // connected -> disconnect
    setShowPopup(true);
  };

  const disconnectEmailSync = async () => {
    gtmEsyncDisconnectConfirm({ method: ConnectEmailMethod.Toggle });
    logEvent('esync_disconnect_confirm', undefined, { method: ConnectEmailMethod.Toggle });
    setShowPopup(false);
    await toggleEmailSync();
  };

  return {
    showPopup,
    setShowPopup,
    isToggling,
    handleToggle,
    disconnectEmailSync,
  };
};
